import React, { useEffect, useState } from 'react';
import { Layout, Menu, Typography, Avatar, Modal, Form, Input, message, Dropdown } from 'antd';
import {
  DashboardOutlined,
  AppstoreOutlined,
  FileSearchOutlined,
  CloudUploadOutlined,
  TeamOutlined,
  SafetyOutlined,
  HistoryOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  UserOutlined,
  KeyOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { changePassword as apiChangePassword, updateProfile } from '../services/api';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

export default function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth < 1440 : false
  ));
  const [openKeys, setOpenKeys] = useState(['/versions']);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateUser } = useAuth();
  const [pwdForm] = Form.useForm();
  const [profileForm] = Form.useForm();
  const isAdmin = user?.role === 'admin' || user?.is_super_admin;
  const canAccessComponentVersions = isAdmin || user?.permissions?.component_versions;
  const siderWidth = collapsed ? 72 : 240;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1440) {
        setCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: '仪表盘' },
    {
      key: '/versions',
      icon: <AppstoreOutlined />,
      label: '版本列表',
      children: [
        { key: '/versions/release', label: '发布版本' },
        { key: '/versions/daily', label: '每日版本' },
        { key: '/versions/market', label: '市场版本' },
        ...(canAccessComponentVersions
          ? [{ key: '/versions/components', label: '组件版本' }]
          : []),
      ],
    },
    { key: '/waveforms', icon: <FileSearchOutlined />, label: '波形库' },
    ...(isAdmin
      ? [{ key: '/upload', icon: <CloudUploadOutlined />, label: '版本上传' }]
      : []),
    ...(isAdmin
      ? [
          { key: '/staff', icon: <TeamOutlined />, label: '用户管理' },
          { key: '/devices', icon: <SafetyOutlined />, label: '设备管理' },
          { key: '/audit-logs', icon: <HistoryOutlined />, label: '审计日志' },
        ]
      : []),
  ];

  // 递归查找当前选中的菜单项
  function findMenuItem(items, pathname) {
    for (const item of items) {
      if (item.children) {
        const childMatch = findMenuItem(item.children, pathname);
        if (childMatch) return childMatch;
      }
      if (item.key === '/' ? pathname === '/' : pathname.startsWith(item.key)) {
        return item;
      }
    }
    return null;
  }

  const selectedItem = findMenuItem(menuItems, location.pathname);
  const selectedKey = selectedItem?.key || '/';

  const handleChangePassword = async (values) => {
    setPwdLoading(true);
    try {
      const res = await apiChangePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      if (res.data.success) {
        message.success(res.data.message || '密码修改成功');
        setPwdModalOpen(false);
        pwdForm.resetFields();
      }
    } catch (err) {
      const msg = err.response?.data?.message || '修改密码失败';
      message.error(msg);
    } finally {
      setPwdLoading(false);
    }
  };

  const handleUpdateProfile = async (values) => {
    setProfileLoading(true);
    try {
      const res = await updateProfile({ name: values.name });
      if (res.data.success) {
        message.success(res.data.message || '信息修改成功');
        updateUser(res.data.data);
        setProfileModalOpen(false);
        profileForm.resetFields();
      }
    } catch (err) {
      const msg = err.response?.data?.message || '修改信息失败';
      message.error(msg);
    } finally {
      setProfileLoading(false);
    }
  };

  const dropdownItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '修改信息',
      onClick: () => {
        profileForm.setFieldsValue({ name: user?.name || '' });
        setProfileModalOpen(true);
      },
    },
    {
      key: 'password',
      icon: <KeyOutlined />,
      label: '修改密码',
      onClick: () => setPwdModalOpen(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: logout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        collapsedWidth={72}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, #071b36 0%, #0b2e57 48%, #08213f 100%)',
          boxShadow: '6px 0 24px rgba(7, 27, 54, 0.18)',
        }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? 0 : '0 18px',
          borderBottom: '1px solid rgba(255,255,255,0.10)',
        }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: collapsed ? 0 : 12,
              flexShrink: 0,
              boxShadow: '0 10px 24px rgba(0, 0, 0, 0.18)',
            }}
          >
            <img src="/welzek-logo.svg" alt="为准电子" style={{ width: 25, display: 'block' }} />
          </div>
          {!collapsed && (
            <div>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 700, lineHeight: '20px', letterSpacing: 0 }}>
                为准电子
              </div>
              <div style={{ color: 'rgba(255,255,255,0.58)', fontSize: 11, lineHeight: '16px' }}>
                版本管理系统
              </div>
            </div>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={collapsed ? [] : openKeys}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          onOpenChange={(keys) => setOpenKeys(keys)}
          style={{
            borderRight: 0,
            marginTop: 8,
            padding: '0 8px',
            background: 'transparent',
          }}
        />
        {!collapsed && (
          <div
            style={{
              position: 'absolute',
              bottom: 14,
              left: 18,
              right: 18,
              color: 'rgba(255,255,255,0.40)',
              fontSize: 11,
              textAlign: 'center',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              paddingTop: 12,
            }}
          >
            知无不为 · 匠心求准
          </div>
        )}
      </Sider>
      <Layout
        style={{
          marginLeft: siderWidth,
          width: `calc(100vw - ${siderWidth}px)`,
          minWidth: 0,
          overflow: 'hidden',
          transition: 'all 0.2s',
        }}
      >
        <Header className="app-header" style={{
          height: 62,
          padding: '0 20px',
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(14px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(1, 79, 162, 0.06)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderBottom: '1px solid #e8eef6',
        }}>
          <div className="app-header-main" style={{ display: 'flex', alignItems: 'center' }}>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              style: { fontSize: 17, cursor: 'pointer', marginRight: 14, color: '#014fa2' },
              onClick: () => setCollapsed(!collapsed),
            })}
            <div className="app-header-brand" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 4,
                  height: 20,
                  borderRadius: 2,
                  background: 'linear-gradient(180deg, #014fa2 0%, #ff7f0d 100%)',
                }}
              />
              <div className="app-header-title-block">
                <Title level={4} className="app-header-title" style={{ margin: 0, fontWeight: 700, color: '#172033', lineHeight: 1.2, fontSize: 18 }}>
                  {selectedItem?.label || '版本管理'}
                </Title>
                <Text className="app-header-subtitle" style={{ color: '#61708a', fontSize: 11 }}>
                  Welzek Version Management
                </Text>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ color: '#595959', fontSize: 14 }}>{user?.name || '用户'}</Text>
              <Avatar src={user?.avatar} style={{ backgroundColor: '#014fa2', fontWeight: 600 }}>
                {(user?.name || '用').charAt(0)}
              </Avatar>
            </div>
            <Dropdown menu={{ items: dropdownItems }} placement="bottomRight">
              <SettingOutlined
                style={{ fontSize: 18, color: '#8c8c8c', cursor: 'pointer' }}
                title="设置"
              />
            </Dropdown>
          </div>
        </Header>
        <Content className="app-content" style={{
          margin: 16,
          padding: 0,
          minHeight: 280,
        }}>
          {children}
        </Content>
      </Layout>

      {/* 修改密码 Modal */}
      <Modal
        title="修改密码"
        open={pwdModalOpen}
        onCancel={() => {
          setPwdModalOpen(false);
          pwdForm.resetFields();
        }}
        onOk={() => pwdForm.submit()}
        confirmLoading={pwdLoading}
        maskClosable={false}
      >
        <Form
          form={pwdForm}
          layout="vertical"
          onFinish={handleChangePassword}
          autoComplete="off"
        >
          <Form.Item
            label="原密码"
            name="oldPassword"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password placeholder="请输入原密码" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少6位' },
            ]}
          >
            <Input.Password placeholder="请设置新密码" />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改信息 Modal */}
      <Modal
        title="修改信息"
        open={profileModalOpen}
        onCancel={() => {
          setProfileModalOpen(false);
          profileForm.resetFields();
        }}
        onOk={() => profileForm.submit()}
        confirmLoading={profileLoading}
        maskClosable={false}
      >
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleUpdateProfile}
          autoComplete="off"
        >
          <Form.Item
            label="姓名"
            name="name"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
